import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import BackButton from '../components/BackButton';
import { useDemo } from '../context/DemoContext';
import { BookOpen, ChevronLeft, ChevronRight, Lock, Gamepad2, ScrollText } from 'lucide-react';

const ADMIN_WALLET = 'C2kQkiowaVKZDXeFd7LE84WZJowrpgYA7DrhPpxqY6S4';
const VAULT_UNLOCK_THRESHOLD = 50000000;
const PARAGRAPHS_PER_PAGE = 4;

const LORE_CHAPTERS = [
  {
    chapter: 1,
    title: "The Drip Beneath the Marble",
    cover: "https://customer-assets.emergentagent.com/job_4a607f52-d588-402c-aebb-51cdcad3ed78/artifacts/rv11lzkt_Drip.png",
    content: `The cities of the Upper Circle were built to defy decay.

Their towers rose in pale stone, veined with gold and lit by a soft radiance that never seemed to dim. Markets hummed without interruption. Doors opened without hesitation. Ledgers balanced themselves in perfect symmetry. It was said that nothing in those cities moved without intention and nothing endured without cost.

The cost was Gas.

It coursed unseen through conduits braided behind walls of marble and beneath avenues paved in silver aggregate. It was measured in private halls by robed accountants whose fingers never trembled. It was taxed, licensed, and rationed. Every gesture required it. Every agreement consumed it. Even silence, at times, was said to incur a price.

Gas was not flame, though it shimmered. It was not liquid, though it pooled. It was not magic, though it answered only to those who understood its language.

The Upper Elites understood it completely.

Or so they believed.

Far below the stately promenades and perfumed courtyards lay a second city that did not appear on any sanctioned map. It was a geography of brick and riveted iron, of tunnels cut in earlier centuries and then abandoned when newer foundations demanded deeper roots. Water collected there in unremarkable puddles. Moss found a hold in seams of stone. The air bore the metallic tang of age.

It was in this drowned understructure that the gremlins lived.

They had not always called themselves by that name. The word had been given to them generations before by an official who mistook their movement for vermin and their language for noise. Names, once applied often enough, tend to hold.

The gremlins were small in stature yet resistant in constitution. Their skin bore the tint of old copper and their eyes reflected even the faintest glimmer of light. They moved without announcing themselves and endured what others would refuse to acknowledge.

What they understood, long before the elites would permit such a thought, was that nothing constructed above ever remains entirely contained.

The first drip was noticed by accident.

It fell from a hairline fracture in the vaulted ceiling of a utility chamber long since retired from record. The substance clung to the stone before releasing itself with deliberation. It glowed faintly, as if embarrassed by its own visibility, and collected in a shallow depression worn smooth by decades of runoff.

A young gremlin named Grek'thel was present when it fell.

He had been mapping the tunnels with charcoal salvaged from discarded fuel rods. His curiosity often led him into sections others deemed unstable. He observed the drip as one might observe a minor irregularity in the pattern of rain. It held his attention only because it did not evaporate.

He reached toward it. The others would later insist that he hesitated. He did not.

The fluid burned the outer layer of his skin, but it did not consume him. Instead it vibrated, a tremor passing not through his flesh but through his awareness. For a moment, the chamber seemed wider. The stone more precisely angled. The trickle of distant water resolved itself into a rhythm that suggested order rather than randomness.

Grek'thel withdrew his hand and found that the burn had not blistered. It had altered.

Where the Gas had touched him, the surface of his skin reflected light differently. Not brighter. Sharper.

He said nothing at first. Gremlins valued observation over proclamation. Yet he returned to the chamber the following day, and the next. The drip persisted. Each drop shimmered before falling, as if reconsidering gravity before surrendering to it.

In time, others accompanied him.

They placed a fractured basin beneath the fissure. The basin had once served as a cooling reservoir for a conduit now removed. The Gas gathered there in increments so minute they might have gone unremarked by those who measured in abundance rather than persistence.

The gremlins did not drink it. They did not yet dare.

They studied it.

It responded to proximity. The surface rippled when approached. Symbols half imagined appeared in its sheen then dissolved before they could be traced. When exposed to air for too long it hardened into a translucent film that could be peeled away in delicate sheets. Those sheets hummed faintly when held close to the ear.

Up above, the ledgers began to fluctuate by fractions too small to trigger alarm. An adjustment here. An anomaly there. A rounding measure applied to smooth inconsistency.

No one considered loss.

In the tunnels the basin filled slowly.

Grek'thel was the first to suggest containment beyond passive collection. His voice carried neither urgency nor rebellion. It carried calculation. If Gas could fall, it could be guided. If it could be guided, it could be shaped.

He did not speak of theft. The word implied transgression of ownership. He had never consented to the premise of ownership to begin with.

The fissure widened over weeks, imperceptibly. The drip increased. Not enough to draw attention. Enough to matter.

And beneath the marble and gold, beneath the hymns to efficiency and control, the first reservoir of stolen Gas began to glow in the dark.

No trumpets sounded above. No council was convened.

But in the understructure of the world, a threshold had been crossed.

The gremlins had touched the resource upon which the Upper Circle relied.

They had learned that it could be endured.

Soon they would learn that it could be transformed.`,
  },
  {
    chapter: 2,
    title: "The Chamber of Condensation",
    cover: "https://customer-assets.emergentagent.com/job_4a607f52-d588-402c-aebb-51cdcad3ed78/artifacts/75sp6yfw_Chamber.png",
    content: `The reservoir did not remain a secret by chance. It remained a secret because the Upper Circle had no reason to imagine that anything beneath them possessed either patience or capacity.

Gas was accounted for in volumes too grand to notice absence measured by handfuls. It was discussed in chambers whose ceilings were painted with scenes of conquest and celestial approval. It was priced according to abstractions that assumed inevitability. To the elites, loss implied inefficiency. Inefficiency implied error. Error, in their system, was always human.

The gremlins had never been considered within the parameters of error.

Below, the basin continued to fill.

The Gas, once suspended in pooled stillness, behaved differently than it had in falling drops. It thickened slightly with accumulation, its surface tension tightening as though aware of its own gathered intent. A faint luminescence escaped its edges and traced the stone floor in hairline veins before fading.

Grek'thel spent long hours in observation. He did not touch the pool again without preparation. Gloves were fashioned from treated hide. Masks were stitched from woven filters scavenged from ventilation ducts long unused. The other gremlins watched him with a silence that signaled neither fear nor approval. It was the silence of community awaiting proof.

On the fourteenth night after the basin's placement, the Gas began to tremble without contact.

The trembling was subtle. A ripple that crossed its surface in concentric rings. No vibration disturbed the surrounding water. No stone shifted. The movement belonged to the Gas alone.

Grek'thel lowered a narrow rod of tempered iron into the pool.

The reaction was immediate.

Light climbed the metal in branching filaments, spreading upward toward his hand. He braced for heat and found instead a force akin to pressure behind the eyes, the sensation of standing at the precipice of understanding without language to contain it.

He withdrew the rod. The iron had darkened along its submerged length, etched with patterns too regular to be accidental. Symbols had impressed themselves into the surface, shallow yet precise. They were not letters of any script known in the tunnels, yet they possessed the symmetry of intention.

The gremlins crowded closer.

It was then that the thought ceased to be individual.

Gas, they realized, was not merely consumed. It was shaped before it was spent. Refined. Directed. Transacted. Every elite structure above relied upon the presumption that such shaping required sanctioned architecture and sanctioned minds.

But the Gas had responded to iron without authorization.

It had marked what touched it.

The discovery did not incite celebration. It induced calculation.

Within a deeper vault of the tunnels stood a chamber older than the marble districts above. Its walls were carved from an age when builders favored endurance over polish. The ceiling arched low. Mineral deposits clung like frost to the stone. A narrow channel once used to divert floodwater cut through the center and fed into a cistern at its rear.

It was here the gremlins relocated the basin.

They worked without firelight. Gas emitted enough glow to map the contours of rock. The chamber absorbed the illumination and returned it in softened echoes. Tools were assembled from salvage. A crucible was repaired with patient care. Runes copied from the rod's etched surface were carved by hand into the lip of the cistern.

Grek'thel did not claim authority, yet the others deferred to him. He did not speak in commands. He asked questions to which the only answer was action.

If Gas marked iron, what would it do when confined?

If confined, would it weaken or intensify?

If intensifying, could it be persuaded to hold form?

The first attempt failed quietly.

Gas was poured into the crucible and heated by coals drawn from a reclaimed furnace shaft. It recoiled from flame, thinning to translucence before flashing into vapor that condensed along the chamber ceiling. For hours afterward, droplets fell like cold rain, reassembling uselessly in the basin below.

The second attempt failed more violently.

Stone fragments were introduced as stabilizing agents. The reaction cracked the crucible and sent a burst of concussive force through the chamber, extinguishing every coal and leaving the gremlins stunned but standing.

Failure did not deter them. It refined them.

On the seventh attempt, they altered their approach.

Gas, Grek'thel observed, did not resist containment. It resisted coercion. Every strategy that forced transformation met with collapse. What if shaping required invitation rather than pressure?

They etched the symbols deeper into the chamber walls, not as impositions but as boundaries. They cooled the crucible rather than heating it. They allowed the Gas to pour in at its own velocity, not siphoned, not stirred.

The liquid gathered at the center and hesitated.

For a moment that none of them later described consistently, the chamber seemed to fold inward. Sound withdrew. The ambient drip of water in distant tunnels ceased. Even breath felt suspended in pause.

The Gas thickened.

Not from heat. Not from compression.

From alignment.

Its surface hardened first at the edges, a thin rim forming as if circumscribed by invisible geometry. The center convulsed once, twice, then grew still. Light condensed into a density that no longer flickered.

When it was done, something remained in the crucible that had not existed before.

It was circular, though imperfectly. Its surface bore impressions that mirrored the runes etched into stone, yet they appeared not carved but grown. The object weighed less than stone yet more than metal of equal size. It radiated neither heat nor cold. Its light did not spill outward but seemed to fold back upon itself.

No one reached for it.

The chamber, so long accustomed to damp echoes and subterranean quiet, felt newly occupied.

Grek'thel stepped forward at last. His altered hand hovered above the object before making contact. This time there was no burn. No pressure. Only recognition.

The sensation was not physical.

It was structural.

A comprehension that something once fluid now possessed persistence independent of its source.

Gas had been coaxed into form.

They had not consumed it.

They had forged it.

The Ancient Coin lay in the crucible without inscription of value, without emblem of rule, without signature of authority. It bore no denomination. It declared no allegiance.

Yet every gremlin in that chamber felt the same tremor of realization.

Gas above required permission.

This did not.

Far above, in a hall of mirrored panels, an accountant paused over a ledger that refused to reconcile. The discrepancy was insignificant by all recognized standards. A fractional distortion within acceptable margin.

He adjusted the figure.

In the chamber below, the Coin pulsed once.

The basin went dark.

And in the silence that followed, the gremlins understood that they had crossed from endurance into intervention.

They were no longer merely surviving beneath the marble.

They had altered the equation that sustained it.`,
  },
  {
    chapter: 3,
    title: "The Echo in the Ledgers",
    cover: "https://customer-assets.emergentagent.com/job_4a607f52-d588-402c-aebb-51cdcad3ed78/artifacts/a1v336jc_Echo.png",
    content: `For three days after its forging, the Coin did nothing.

It did not vibrate. It did not glow brighter. It did not fracture or multiply.

It rested within the crucible like an object that had always existed and had merely been uncovered. The chamber returned to its usual sounds. Water resumed its steady passage through the diversion channel. Moss reclaimed its quiet corners of stone.

And yet the silence felt conditional.

The gremlins did not disperse. They took turns watching it, not out of fear but out of attentiveness. Grek'thel insisted it be left unhandled unless necessary. The act of forging had required restraint. Whatever followed, he believed, would require the same.

On the fourth night, the first anomaly occurred.

The Coin did not emit light.

The chamber did.

A faint luminescence rippled across the walls in patterns that mirrored the etched runes. Not reflection. Not refraction. It was as though the stone remembered having been cut and now rehearsed the motion internally.

The Coin remained still while the chamber awakened around it.

One of the younger gremlins, tasked with maintaining ventilation channels that connected the lower vaults to older shafts above, was the first to notice a secondary effect. Sections of conduit through which Gas occasionally seeped began to hum. The sound was low and irregular, like distant machinery attempting to correct itself.

At first the hum faded.

Then it returned.

This time it persisted.

Far above, in the uppermost administrative hall of the Eastern Quarter, a sequence of routine transactions stalled mid-recording. No alarms sounded. The interruption lasted less than a breath. By the time attendants reviewed the flow, everything had resumed.

But the ledgers remembered.

A microfracture in accounting appeared, subtle as an altered heartbeat. A transaction had executed without consuming the anticipated volume. The discrepancy was small enough to be folded into rounding adjustments, yet persistent enough to resist full correction.

The accountants attributed it to recalibration lag in the conduits beneath the district. They dispatched inspectors.

The inspectors found no breach.

Below, the Coin pulsed again.

This pulse was stronger. It did not distort the chamber. It did something subtler.

Every gremlin within the vault felt a shift in spatial awareness. The tunnels that connected to their chamber no longer felt merely like corridors of stone. They felt networked.

That was the nearest word any of them would later apply.

Lines of relation extended outward from the Coin, not as physical filaments but as comprehensions of adjacency. Where Gas flowed above, where conduits twisted through forgotten catacombs, where fractures in elite architecture intersected neglected masonry, the Coin seemed to map them.

Not visually.

Structurally.

Grek'thel approached the crucible and placed both hands upon its rim without touching the Coin itself. He closed his eyes.

Images arose unbidden. Not images in the sense of picture but sequences of causality. A chamber above where Gas condensed before distribution. A regulator valve adjusted by a bored attendant. A vein of conduit thin enough to be vulnerable.

He opened his eyes and turned to the others.

"It does not store power," he said quietly. "It redistributes pressure."

The distinction mattered.

The elites believed Gas was finite but governable. Measured. Tiered. Controlled by authority. The Coin had not increased the total volume within the system.

It had introduced instability into the hierarchy of its flow.

The hum in the conduits intensified over the next several days. Gremlins assigned to upper boundary tunnels reported intermittent surges where none had existed before. Small rivulets of Gas collected in places previously dry.

Above, the adjustments grew less dismissible.

Three districts reported irregular consumption patterns. Not deficits. Variations. Some sectors required less to complete tasks previously fixed at rigid costs. Others fluctuated unpredictably for brief intervals before returning to norm.

Meetings were convened.

Explanations were drafted.

Revisions were applied to distribution algorithms.

No one suspected agency.

Beneath the marble, the gremlins gathered in the chamber of condensation.

The Coin did not glow brighter. It did not multiply.

But its presence had entered the world above without revealing its shape.

And in that quiet intrusion lay the beginning of pursuit.`,
  },
  {
    chapter: 4,
    title: "Chapter Coming Soon",
    cover: null,
    content: `The story continues...

The next chapter of the gremlin saga is being written in the depths of the sewers.

Return soon to discover what happens next.

The gremlins are patient. So must you be.`,
  }
];

// Split chapter content into book pages
const splitIntoPages = (content) => {
  const paragraphs = content.split('\n\n').filter(p => p.trim());
  const pages = [];
  for (let i = 0; i < paragraphs.length; i += PARAGRAPHS_PER_PAGE) {
    pages.push(paragraphs.slice(i, i + PARAGRAPHS_PER_PAGE));
  }
  return pages;
};

// ── The Grimoire Book ──
const Grimoire = ({ chapter, onPrev, onNext, hasPrev, hasNext, chapterIndex, totalChapters }) => {
  const [pageIndex, setPageIndex] = useState(-1);
  const [direction, setDirection] = useState(0);
  const [renderKey, setRenderKey] = useState(0);
  const pages = splitIntoPages(chapter.content);
  const totalPages = pages.length;
  const isOnCover = pageIndex === -1;

  // Reset to cover when chapter changes — use renderKey to force clean re-render
  useEffect(() => {
    setPageIndex(-1);
    setDirection(0);
    setRenderKey(k => k + 1);
  }, [chapterIndex]);

  const goNext = useCallback(() => {
    setDirection(1);
    if (isOnCover) { setPageIndex(0); }
    else if (pageIndex < totalPages - 1) { setPageIndex(p => p + 1); }
    else if (hasNext) { onNext(); }
  }, [isOnCover, pageIndex, totalPages, hasNext, onNext]);

  const goPrev = useCallback(() => {
    setDirection(-1);
    if (pageIndex === 0) { setPageIndex(-1); }
    else if (pageIndex > 0) { setPageIndex(p => p - 1); }
    else if (hasPrev) { onPrev(); }
  }, [pageIndex, hasPrev, onPrev]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goNext, goPrev]);

  const slideVariants = {
    enter: (d) => ({ x: d > 0 ? 300 : -300, opacity: 0, scale: 0.95 }),
    center: { x: 0, opacity: 1, scale: 1 },
    exit: (d) => ({ x: d > 0 ? -300 : 300, opacity: 0, scale: 0.95 }),
  };

  return (
    <div className="w-full" data-testid="grimoire">
      {/* The Book */}
      <div className="relative mx-auto" style={{ maxWidth: '950px' }}>

        {/* Outer leather binding */}
        <div className="relative rounded-sm overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #201a0e 0%, #161008 50%, #201a0e 100%)',
            border: '2px solid rgba(180,140,70,0.2)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.7), 0 0 40px rgba(180,140,70,0.06), inset 0 0 40px rgba(0,0,0,0.3)',
            padding: '12px',
          }}>

          {/* Inner parchment */}
          <div className="relative overflow-hidden rounded-sm" style={{ background: 'linear-gradient(135deg, #1a1610 0%, #15120c 50%, #1a1610 100%)', minHeight: '500px' }}>

            {/* Parchment texture overlay */}
            <div className="absolute inset-0 pointer-events-none z-[1] opacity-[0.06]"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.6' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />

            {/* Warm ambient glow */}
            <div className="absolute inset-0 pointer-events-none z-[1]"
              style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(200,160,80,0.06), transparent 70%)' }} />

            {/* Edge vignette — softer */}
            <div className="absolute inset-0 pointer-events-none z-[2]"
              style={{ boxShadow: 'inset 0 0 60px rgba(0,0,0,0.4), inset 0 0 120px rgba(0,0,0,0.2)' }} />

            {/* ═══ DESKTOP: Two-page spread ═══ */}
            <div className="hidden md:grid md:grid-cols-2 relative z-10" style={{ minHeight: '520px' }}>

              {/* Book spine divider */}
              <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[3px] z-20"
                style={{ background: 'linear-gradient(180deg, rgba(180,140,70,0.15), rgba(180,140,70,0.35), rgba(180,140,70,0.15))' }}>
                <div className="absolute inset-0" style={{ boxShadow: '-6px 0 15px rgba(0,0,0,0.5), 6px 0 15px rgba(0,0,0,0.5)' }} />
              </div>

              {/* LEFT PAGE — Cover art or even pages */}
              <div className="p-6 lg:p-8 flex flex-col relative">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div key={`left-${renderKey}-${pageIndex}`} custom={direction}
                    variants={slideVariants} initial="enter" animate="center" exit="exit"
                    transition={{ duration: 0.35, ease: 'easeOut' }}
                    className="flex-1 flex flex-col">
                    {isOnCover ? (
                      // Chapter cover art
                      <div className="flex-1 flex flex-col items-center justify-center">
                        {chapter.cover ? (
                          <img src={chapter.cover} alt={chapter.title}
                            className="max-w-full max-h-[380px] object-contain"
                            style={{ filter: 'sepia(0.15) brightness(0.9)', border: '1px solid rgba(120,90,40,0.15)' }} />
                        ) : (
                          <div className="w-48 h-64 flex items-center justify-center"
                            style={{ background: 'rgba(120,90,40,0.03)', border: '1px dashed rgba(120,90,40,0.12)' }}>
                            <BookOpen className="w-10 h-10" style={{ color: 'rgba(120,90,40,0.15)' }} />
                          </div>
                        )}
                      </div>
                    ) : (
                      // Text page (left side context)
                      <div className="flex-1 flex flex-col justify-center">
                        <div className="mb-4">
                          <p className="text-[9px] tracking-[0.2em] uppercase" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(200,170,100,0.5)' }}>
                            Chapter {chapter.chapter}
                          </p>
                          <p className="text-sm font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(230,200,130,0.7)' }}>
                            {chapter.title}
                          </p>
                        </div>
                        {chapter.cover && (
                          <img src={chapter.cover} alt="" className="w-full max-h-[280px] object-contain opacity-40"
                            style={{ filter: 'sepia(0.3) brightness(0.7)' }} />
                        )}
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* RIGHT PAGE — Title or text */}
              <div className="p-6 lg:p-8 flex flex-col relative">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div key={`right-${renderKey}-${pageIndex}`} custom={direction}
                    variants={slideVariants} initial="enter" animate="center" exit="exit"
                    transition={{ duration: 0.35, ease: 'easeOut', delay: 0.05 }}
                    className="flex-1 flex flex-col">
                    {isOnCover ? (
                      // Title page
                      <div className="flex-1 flex flex-col items-center justify-center text-center">
                        <p className="text-[9px] tracking-[0.35em] uppercase mb-3"
                          style={{ fontFamily: "'Cinzel', serif", color: 'rgba(200,170,100,0.6)' }}>
                          Chapter {chapter.chapter}
                        </p>
                        <h2 className="text-2xl lg:text-3xl font-bold mb-4 leading-tight"
                          style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif", color: 'rgba(240,210,140,0.9)', filter: 'drop-shadow(0 0 15px rgba(200,160,80,0.3))' }}>
                          {chapter.title}
                        </h2>
                        <div className="w-24 h-[1px] mb-4" style={{ background: 'linear-gradient(90deg, transparent, rgba(200,170,100,0.5), transparent)' }} />
                        <p className="text-[11px] leading-relaxed max-w-xs" style={{ fontFamily: "'Georgia', serif", color: 'rgba(220,200,170,0.55)' }}>
                          {chapter.content.split('\n\n')[0].substring(0, 150)}...
                        </p>
                        <motion.p className="mt-6 text-[9px] tracking-[0.2em] uppercase"
                          style={{ fontFamily: "'Courier New', monospace", color: 'rgba(200,170,100,0.4)' }}
                          animate={{ opacity: [0.2, 0.5, 0.2] }} transition={{ duration: 2, repeat: Infinity }}>
                          Turn the page
                        </motion.p>
                      </div>
                    ) : (
                      // Text content
                      <div className="flex-1 flex flex-col justify-between">
                        <div className="space-y-4 flex-1">
                          {pages[pageIndex]?.map((p, i) => (
                            <motion.p key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.08 }}
                              className="text-[13px] leading-[1.9]"
                              style={{ fontFamily: "'Georgia', serif", color: 'rgba(220,205,180,0.75)' }}>
                              {p}
                            </motion.p>
                          ))}
                        </div>
                        <p className="text-center text-[9px] mt-4 pt-3"
                          style={{ fontFamily: "'Courier New', monospace", color: 'rgba(200,170,100,0.35)', borderTop: '1px solid rgba(200,170,100,0.1)' }}>
                          {pageIndex + 1} / {totalPages}
                        </p>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* ═══ MOBILE: Single page ═══ */}
            <div className="md:hidden relative z-10" style={{ minHeight: '450px' }}>
              <div className="p-5">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div key={`mobile-${renderKey}-${pageIndex}`} custom={direction}
                    variants={slideVariants} initial="enter" animate="center" exit="exit"
                    transition={{ duration: 0.3, ease: 'easeOut' }}>
                    {isOnCover ? (
                      <div className="flex flex-col items-center text-center py-6">
                        {chapter.cover && (
                          <img src={chapter.cover} alt={chapter.title}
                            className="w-full max-h-[220px] object-contain mb-5"
                            style={{ filter: 'sepia(0.15) brightness(0.9)', border: '1px solid rgba(120,90,40,0.15)' }} />
                        )}
                        <p className="text-[9px] tracking-[0.35em] uppercase mb-2"
                          style={{ fontFamily: "'Cinzel', serif", color: 'rgba(180,140,70,0.4)' }}>
                          Chapter {chapter.chapter}
                        </p>
                        <h2 className="text-xl font-bold mb-3"
                          style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif", color: 'rgba(220,190,120,0.8)' }}>
                          {chapter.title}
                        </h2>
                        <div className="w-16 h-[1px] mb-3" style={{ background: 'linear-gradient(90deg, transparent, rgba(180,140,70,0.3), transparent)' }} />
                        <motion.p className="text-[9px] tracking-[0.15em] uppercase"
                          style={{ fontFamily: "'Courier New', monospace", color: 'rgba(180,140,70,0.25)' }}
                          animate={{ opacity: [0.2, 0.5, 0.2] }} transition={{ duration: 2, repeat: Infinity }}>
                          Swipe to begin
                        </motion.p>
                      </div>
                    ) : (
                      <div className="flex flex-col min-h-[380px]">
                        <p className="text-[8px] tracking-[0.15em] uppercase mb-4"
                          style={{ fontFamily: "'Cinzel', serif", color: 'rgba(180,140,70,0.25)' }}>
                          Ch. {chapter.chapter} - {chapter.title}
                        </p>
                        <div className="space-y-4 flex-1">
                          {pages[pageIndex]?.map((p, i) => (
                            <motion.p key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                              transition={{ delay: i * 0.06 }}
                              className="text-[12px] leading-[1.85]"
                              style={{ fontFamily: "'Georgia', serif", color: 'rgba(220,205,180,0.7)' }}>
                              {p}
                            </motion.p>
                          ))}
                        </div>
                        <p className="text-center text-[9px] mt-4 pt-3"
                          style={{ fontFamily: "'Courier New', monospace", color: 'rgba(200,170,100,0.35)', borderTop: '1px solid rgba(200,170,100,0.1)' }}>
                          {pageIndex + 1} / {totalPages}
                        </p>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation arrows */}
        <div className="flex items-center justify-between mt-4">
          <button onClick={goPrev} disabled={isOnCover && !hasPrev}
            className="flex items-center gap-2 px-4 py-2 transition-all"
            style={{
              fontFamily: "'Cinzel', serif", fontSize: '11px', letterSpacing: '0.1em',
              color: (isOnCover && !hasPrev) ? 'rgba(180,140,70,0.1)' : 'rgba(180,140,70,0.45)',
              border: `1px solid ${(isOnCover && !hasPrev) ? 'rgba(180,140,70,0.05)' : 'rgba(180,140,70,0.15)'}`,
              background: (isOnCover && !hasPrev) ? 'transparent' : 'rgba(180,140,70,0.03)',
            }} data-testid="book-prev">
            <ChevronLeft className="w-3.5 h-3.5" /> PREV
          </button>

          <span className="text-[9px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(180,140,70,0.15)' }}>
            {isOnCover ? `Chapter ${chapter.chapter} of ${totalChapters}` : `Page ${pageIndex + 1} of ${totalPages}`}
          </span>

          <button onClick={goNext} disabled={pageIndex === totalPages - 1 && !hasNext}
            className="flex items-center gap-2 px-4 py-2 transition-all"
            style={{
              fontFamily: "'Cinzel', serif", fontSize: '11px', letterSpacing: '0.1em',
              color: (pageIndex === totalPages - 1 && !hasNext) ? 'rgba(180,140,70,0.1)' : 'rgba(180,140,70,0.5)',
              border: `1px solid ${(pageIndex === totalPages - 1 && !hasNext) ? 'rgba(180,140,70,0.05)' : 'rgba(180,140,70,0.2)'}`,
              background: (pageIndex === totalPages - 1 && !hasNext) ? 'transparent' : 'rgba(180,140,70,0.04)',
            }} data-testid="book-next">
            NEXT <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ──
const LorePage = () => {
  const navigate = useNavigate();
  const { publicKey } = useWallet();
  const walletAddress = publicKey?.toBase58() || '';
  const isAdmin = walletAddress === ADMIN_WALLET;
  const [currentChapter, setCurrentChapter] = useState(0);
  const [totalFeesStolen, setTotalFeesStolen] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { isDemo: isGlobalDemo } = useDemo();
  const isDemoMode = new URLSearchParams(window.location.search).get('demo') === 'true' || isGlobalDemo;

  useEffect(() => {
    const fetchTotalFees = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/arcade/stats`);
        if (r.ok) { const d = await r.json(); setTotalFeesStolen(d.total_fees_stolen || 0); }
      } catch {} finally { setIsLoading(false); }
    };
    fetchTotalFees();
    const iv = setInterval(fetchTotalFees, 5000);
    return () => clearInterval(iv);
  }, []);

  const isUnlocked = isDemoMode || isAdmin || totalFeesStolen >= VAULT_UNLOCK_THRESHOLD;
  const progress = Math.min((totalFeesStolen / VAULT_UNLOCK_THRESHOLD) * 100, 100);
  const formatNumber = (n) => n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : n.toLocaleString();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080604' }}>
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
          <BookOpen className="w-8 h-8" style={{ color: 'rgba(180,130,60,0.5)' }} />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white relative" data-testid="lore-page" style={{ background: '#080604' }}>
      {/* BG */}
      <div className="fixed inset-0 z-0" style={{ background: 'radial-gradient(ellipse at 50% 20%, #12100a 0%, #0c0a06 40%, #080604 100%)' }} />
      <div className="fixed inset-0 z-[1] pointer-events-none">
        <motion.div className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 30% 30%, rgba(180,130,60,0.05) 0%, transparent 45%)' }}
          animate={{ opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 5, repeat: Infinity }} />
      </div>
      <div className="fixed inset-0 z-[60] pointer-events-none mix-blend-overlay" style={{
        background: 'linear-gradient(rgba(18,16,16,0) 50%, rgba(0,0,0,0.15) 50%)', backgroundSize: '100% 4px', opacity: 0.08,
      }} />
      {/* Dust */}
      <div className="fixed inset-0 z-[2] pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <motion.div key={i} className="absolute rounded-full"
            style={{ left: `${5 + i * 12}%`, bottom: '-8px', width: '2px', height: '2px', background: '#b4883c', boxShadow: '0 0 5px rgba(180,136,60,0.5)' }}
            animate={{ y: [0, -500], opacity: [0, 0.35, 0] }}
            transition={{ duration: 7 + i, delay: i * 0.7, repeat: Infinity, ease: 'easeOut' }} />
        ))}
      </div>

      <BackButton to="/" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-6 lg:px-12 pt-20 pb-16">
        {/* Header */}
        <div className="mb-8 text-center">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] uppercase tracking-[0.5em] mb-1"
            style={{ fontFamily: "'Cinzel', serif", color: 'rgba(212,168,92,0.6)' }}>Ancient Texts From Beneath the Marble</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-wider"
            style={{
              fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
              background: 'linear-gradient(180deg, #f5e6c8 0%, #d4a85c 30%, #b4883c 60%, #d4a85c 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 0 25px rgba(212,168,92,0.4)) drop-shadow(0 0 50px rgba(180,136,60,0.2))',
            }}
            data-testid="lore-title">
            The Book of Gremlins
          </motion.h1>
          <motion.div initial={{ width: 0 }} animate={{ width: '200px' }} transition={{ duration: 0.8, delay: 0.3 }}
            className="mx-auto mt-3 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,92,0.5), transparent)' }} />
        </div>

        {!isUnlocked ? (
          /* ═══ LOCKED ═══ */
          <div className="max-w-md mx-auto text-center">
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="mb-6">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto"
                style={{ background: 'rgba(180,130,60,0.04)', border: '2px solid rgba(180,130,60,0.15)' }}>
                <Lock className="w-7 h-7" style={{ color: 'rgba(180,130,60,0.4)' }} />
              </div>
            </motion.div>
            <h2 className="text-xl md:text-2xl font-bold mb-2" style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif", color: 'rgba(220,180,80,0.85)' }} data-testid="lore-locked-title">The Vault is Sealed</h2>
            <p className="text-xs max-w-sm mx-auto mb-6" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(180,130,60,0.3)' }}>
              The Book of Gremlins remains locked within the Sewer Archives. The community must steal enough fees to break the ancient seal.
            </p>
            <div className="max-w-xs mx-auto mb-4" data-testid="lore-progress-card">
              <div className="flex justify-between mb-1 text-[9px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(180,130,60,0.35)' }}>
                <span>Seal Integrity</span>
                <span>{formatNumber(totalFeesStolen)} / {formatNumber(VAULT_UNLOCK_THRESHOLD)}</span>
              </div>
              <div className="h-2 relative overflow-hidden" style={{ background: 'rgba(180,130,60,0.04)', border: '1px solid rgba(180,130,60,0.08)' }}>
                <motion.div className="absolute inset-y-0 left-0" style={{ background: 'linear-gradient(90deg, rgba(180,130,60,0.5), rgba(220,180,80,0.7))' }}
                  initial={{ width: 0 }} animate={{ width: `${Math.max(progress, 1)}%` }} transition={{ duration: 1.5, delay: 0.5 }} />
              </div>
              <p className="text-[9px] text-right mt-1" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(180,130,60,0.25)' }}>{progress.toFixed(1)}%</p>
            </div>
            <button onClick={() => navigate('/arcade')} className="inline-flex items-center gap-2 px-6 py-2 text-xs font-bold tracking-wider uppercase"
              style={{ fontFamily: "'Cinzel', serif", background: 'rgba(180,130,60,0.08)', border: '1px solid rgba(180,130,60,0.2)', color: 'rgba(180,130,60,0.6)' }}
              data-testid="lore-arcade-cta">
              <Gamepad2 className="w-3.5 h-3.5" /> Play Arcade to Break the Seal
            </button>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-2">
              {LORE_CHAPTERS.map((ch, i) => (
                <div key={i} className="p-3 text-center" style={{ background: 'rgba(180,130,60,0.02)', border: '1px solid rgba(180,130,60,0.06)' }}>
                  <p className="text-[9px] uppercase" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(180,130,60,0.25)' }}>Ch. {ch.chapter}</p>
                  <p className="text-[10px] font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(180,130,60,0.35)' }}>{ch.title}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* ═══ UNLOCKED — Grimoire ═══ */
          <>
            {/* Chapter selector */}
            <div className="flex items-center justify-center gap-2 mb-6 flex-wrap">
              {LORE_CHAPTERS.map((ch, i) => (
                <button key={i} onClick={() => setCurrentChapter(i)} data-testid={`lore-chapter-${i}`}
                  className="px-3 py-1.5 text-[10px] tracking-wider uppercase transition-all"
                  style={{
                    fontFamily: "'Cinzel', serif",
                    background: i === currentChapter ? 'rgba(180,130,60,0.1)' : 'transparent',
                    border: `1px solid ${i === currentChapter ? 'rgba(180,130,60,0.3)' : 'rgba(180,130,60,0.08)'}`,
                    color: i === currentChapter ? 'rgba(220,190,120,0.8)' : 'rgba(180,130,60,0.25)',
                    boxShadow: i === currentChapter ? '0 0 12px rgba(180,130,60,0.08)' : 'none',
                  }}>
                  Ch. {ch.chapter}
                </button>
              ))}
            </div>

            <Grimoire
              chapter={LORE_CHAPTERS[currentChapter]}
              chapterIndex={currentChapter}
              totalChapters={LORE_CHAPTERS.length}
              onNext={() => setCurrentChapter(p => Math.min(p + 1, LORE_CHAPTERS.length - 1))}
              onPrev={() => setCurrentChapter(p => Math.max(p - 1, 0))}
              hasNext={currentChapter < LORE_CHAPTERS.length - 1}
              hasPrev={currentChapter > 0}
            />

            <p className="text-center text-[9px] tracking-[0.2em] uppercase mt-8"
              style={{ fontFamily: "'Courier New', monospace", color: 'rgba(180,130,60,0.1)' }}>The Sewer Archives</p>
          </>
        )}
      </div>
    </div>
  );
};

export default LorePage;
